import { Dialog, Transition } from "@headlessui/react"
import IntakeQuestionCard from "components/BookingPageComponents/IntakeFormCard"
import GenericSwitch from "components/GenericSwitch"
import Translation from "components/Translation"
import { useAppContext } from "context/AppContext"
import { useAppErrorApiContext } from "context/AppErrorContext"
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from "react"
import { FormQuestionResponse, IntakeAnswer, IntakeClientResponse, IntakeForm, mapAnswersToResponseDTO, mapToFormQuestionResponse, mapToResponseList, QuestionDisplayType } from "types/intake-forms"
import { putApiRequest } from "utils/fetch-helper"
import { getTranslation } from "utils/language-helper"
import DialogModal from "../Dialog"

export type ViewResponseStatus = {
    show: boolean
    is_edit?: boolean
}

type ViewResponseProps = {
    status: ViewResponseStatus
    toggleStatus(status: ViewResponseStatus): void
    form: IntakeForm
    response?: IntakeClientResponse
    show_client_details?: boolean
    handleEditForm?: () => void
    view_form_index?: number
    setViewFormIndex?: Dispatch<SetStateAction<number>>
    total_forms_count?: number
    is_allow_edit?: boolean
    handleRefetch?: () => void
}

export default function ViewResponse(props: ViewResponseProps) { //TODO: convert to a modal with attributes so it can be initialized in just one area and reused everywhere
    const {
        status,
        toggleStatus,
        form,
        response,
        show_client_details,
        handleEditForm,
        view_form_index,
        setViewFormIndex,
        total_forms_count,
        is_allow_edit,
        handleRefetch
    } = props
    const { locale, translations, setGlobalLoading } = useAppContext()
    const { setGlobalError } = useAppErrorApiContext()
    const [question_responses, setQuestionResponses] = useState<FormQuestionResponse[] | undefined>(undefined)
    const [response_list, setResponseList] = useState<Record<string, IntakeAnswer[]>>({})
    const [show_success, toggleSuccess] = useState<boolean>(false)
    const [success_message, setSuccessMessage] = useState<string>(getTranslation("forms.successfully_updated_response", translations))
    const [is_dirty, toggleDirty] = useState<boolean>(false)

    function handleClose() {
        toggleStatus({ show: false, is_edit: false })
        if (setViewFormIndex) {
            setTimeout(() => { setViewFormIndex(0) }, 100)
        }
    }

    useEffect(() => {
        if (form && response) {
            setQuestionResponses(mapToFormQuestionResponse(form, response.response))
            if (is_allow_edit) {
                setResponseList(mapToResponseList(form, response.response))
            }
        } else {
            setQuestionResponses(undefined)
            setResponseList({})
        }
    }, [form, response])

    useEffect(() => {
        setSuccessMessage(getTranslation("forms.successfully_updated_response", translations))
    }, [translations["forms.successfully_updated_response"]])

    async function handleUpdateResponse() {
        try {
            setGlobalLoading(true, true)
            let payload = mapAnswersToResponseDTO(form, response_list, response?.response)
            let res = await putApiRequest('/v1/intake-forms/responses', payload)
            if (res?.ok) {
                toggleSuccess(true)
                handleRefetch && handleRefetch()
            }
        } catch (error) {
            setGlobalError(error)
        } finally {
            setGlobalLoading(false, true)
        }
    }

    return (
        <Transition
            appear
            show={status.show}
            as="div"
            enter="transition-opacity duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className="z-[200]"
        >
            {response && question_responses &&
                <Dialog
                    as="div"
                    className="fixed inset-0 overflow-y-auto z-[200]"
                    onClose={handleClose}
                    hidden={!status.show}
                >
                    <DialogModal
                        show={show_success}
                        prompt_message={success_message}
                        onCloseModal={() => {
                            toggleDirty(false)
                            toggleStatus({ show: false, is_edit: false })
                            setTimeout(() => {
                                if (setViewFormIndex) {
                                    setViewFormIndex(0)
                                }
                                toggleSuccess(false)
                            }, 100)
                        }}
                    />
                    <div hidden={show_success} className="min-h-screen text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Dialog.Overlay className="fixed inset-0 backdrop-filter backdrop-brightness-50" />
                        </Transition.Child>

                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <div className='inline-block w-full h-screen z-[200]'>
                                <div className="max-w-2xl mx-auto z-[200] px-12 py-10 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                                    <div className="w-full flex flex-col">
                                        <div className="flex flex-row w-full mb-10 text-black-one justify-between">
                                            <Translation
                                                content_key="booking.responses"
                                                render_as="span"
                                                className="text-[32px] font-medium  mt-2"
                                            />
                                            <button className="feather-x text-xl"
                                                onClick={handleClose}
                                            />
                                        </div>
                                        {show_client_details &&
                                            <div className="flex flex-row w-full break-words grid grid-cols-5 gap-x-2 py-5 px-4 bg-blue-50 rounded-lg font-medium text-sm text-dark">
                                                <div className="flex flex-col gap-y-2">
                                                    <Translation
                                                        content_key="booking.first_name"
                                                        render_as="span"
                                                        className="uppercase text-secondary-blue font-bold text-[10px]"
                                                    />
                                                    {response.client.user.first_name}
                                                </div>
                                                <div className="flex flex-col gap-y-2">
                                                    <Translation
                                                        content_key="booking.last_name"
                                                        render_as="span"
                                                        className="uppercase text-secondary-blue font-bold text-[10px]"
                                                    />
                                                    {response.client.user.last_name}
                                                </div>
                                                <div className="flex flex-col gap-y-2 col-span-2">
                                                    <Translation
                                                        content_key="booking.email"
                                                        render_as="span"
                                                        className="uppercase text-secondary-blue font-bold text-[10px]"
                                                    />
                                                    {response.client.user.email}
                                                </div>
                                                <div className="flex flex-col gap-y-2">
                                                    <Translation
                                                        content_key="booking.phone"
                                                        render_as="span"
                                                        className="uppercase text-secondary-blue font-bold text-[10px]"
                                                    />
                                                    {response.client.user.phone}
                                                </div>
                                            </div>
                                        }
                                        {response.response.date_taken &&
                                            <div className="flex flex-row w-full items-center text-center text-sm font-bold text-[#58595B] mt-8 mb-4">
                                                <Translation
                                                    content_key="booking.response_received"
                                                    render_as="span"
                                                    className="text-zinc-500 font-[450]"
                                                />
                                                &nbsp;
                                                {response.response.date_taken?.toLocaleDateString(locale, {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: 'numeric',
                                                    minute: 'numeric',
                                                    hour12: true
                                                })}
                                            </div>
                                        }
                                        {is_allow_edit &&
                                            <div className="flex flex-row mb-6 text-center items-center">
                                                <GenericSwitch
                                                    is_on={!!status?.is_edit}
                                                    toggleOn={(toggle: boolean) => { toggleStatus({ ...status, is_edit: toggle }) }}
                                                    label="forms.edit_client_responses"
                                                />
                                            </div>
                                        }
                                        {status.is_edit
                                            ?
                                            <div className="flex flex-col w-full">
                                                {form.questions?.map((question, index) => {
                                                    return (
                                                        <IntakeQuestionCard
                                                            key={question.id}
                                                            intake_question={question}
                                                            ordinal={index + 1}
                                                            answers={question.id ? response_list[question.id] : []}
                                                            setAnswers={(answers: IntakeAnswer[]) => {
                                                                if (question.id) {
                                                                    setResponseList({
                                                                        ...response_list,
                                                                        [question.id]: answers
                                                                    })
                                                                    if (!is_dirty) {
                                                                        toggleDirty(true)
                                                                    }
                                                                }
                                                            }}
                                                            disabled={question.asked_once}
                                                        />
                                                    )
                                                })}
                                            </div>
                                            :
                                            <div className="flex flex-col w-full rounded-2xl mb-5 p-6 gap-y-4 bg-[#F9FAFE] border border-gray-three">
                                                {question_responses.map((question_response, index) => {
                                                    let ordinal: number = index + 1
                                                    return (
                                                        <>
                                                            <div className="flex flex-col w-full gap-y-3">
                                                                <div className="flex flex-row w-full text-black-one text-sm font-[450]">
                                                                    {ordinal}. {question_response.question}
                                                                </div>
                                                                {question_response.display_type != QuestionDisplayType.FILE
                                                                    ?
                                                                    <div className="flex flex-row w-full font-medium text-sm text-primary">
                                                                        {question_response.responses?.join(", ")}
                                                                    </div>
                                                                    :
                                                                    <img
                                                                        className="flex flex-row max-w-full w-fit h-[150px] object-contain"
                                                                        src={question_response.responses?.[0] || ""}
                                                                        alt=""
                                                                    />
                                                                }

                                                            </div>
                                                            {ordinal != question_responses.length && <hr />}
                                                        </>
                                                    )
                                                })}
                                            </div>
                                        }

                                        {view_form_index != undefined && setViewFormIndex && total_forms_count != undefined &&
                                            <div className="flex flex-row w-full items-center justify-center text-xl">
                                                <button
                                                    className="feather-chevrons-left disabled:opacity-50"
                                                    disabled={view_form_index < 1}
                                                    onClick={() => {
                                                        setViewFormIndex(0)
                                                    }}
                                                />
                                                <button
                                                    className="feather-chevron-left disabled:opacity-50"
                                                    disabled={view_form_index < 1}
                                                    onClick={() => {
                                                        setViewFormIndex(view_form_index - 1)
                                                    }}
                                                />
                                                <div>
                                                    {view_form_index + 1}
                                                </div>
                                                <button
                                                    className="feather-chevron-right disabled:opacity-50"
                                                    disabled={total_forms_count <= view_form_index + 1}
                                                    onClick={() => {
                                                        setViewFormIndex(view_form_index + 1)
                                                    }}
                                                />
                                                <button
                                                    className="feather-chevrons-right disabled:opacity-50"
                                                    disabled={total_forms_count <= view_form_index + 1}
                                                    onClick={() => {
                                                        setViewFormIndex(total_forms_count - 1)
                                                    }}
                                                />
                                            </div>
                                        }
                                        <div className="flex flex-row w-full gap-x-4 mt-5 text-base font-[450] justify-end book-form-btn">
                                            <button
                                                className="flex items-center py-3.5 justify-center w-1/4 bg-white text-black-one border border-gray-300 rounded-lg"
                                                onClick={handleClose}
                                            >
                                                <Translation
                                                    content_key="booking.close"
                                                />
                                            </button>
                                            {handleEditForm &&
                                                <button
                                                    className="py-3.5 flex items-center justify-center w-1/4 bg-primary text-white rounded-lg"
                                                    onClick={handleEditForm}
                                                >
                                                    <Translation
                                                        content_key="booking.edit_responses"
                                                    />
                                                </button>
                                            }
                                            {is_allow_edit && status.is_edit &&
                                                <button
                                                    className="flex items-center justify-center w-1/4 bg-primary text-white rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
                                                    disabled={!is_dirty}
                                                    onClick={handleUpdateResponse}
                                                >
                                                    <Translation
                                                        content_key="booking.save"
                                                    />
                                                </button>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </Dialog>
            }
        </Transition>
    )
}